import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconDropside } from '@components/icons/dropside';
import { SizedRelativeSpinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { type QuestionAnswer, type ToolData } from 'api/ai-builder/requests';

import { AiBuilderEnterPlanMode } from '../../ai-builder-enter-plan-mode';
import { AiBuilderLinkPreview } from '../../ai-builder-link-preview';
import { AiBuilderMarketplaceAuth } from '../../ai-builder-marketplace-auth';
import { AiBuilderPlan } from '../../ai-builder-plan';
import { AiBuilderQuestions } from '../../ai-builder-questions';
import { AiBuilderResourcePicker } from '../../ai-builder-resource-picker';
import { AiBuilderReviewPrompt } from '../../ai-builder-review-prompt';
import type { ToolStatus } from '../../hooks';

const MESSAGE_GAP = pxToRem(12);
const TOOL_RAIL_COLUMN_WIDTH = pxToRem(20);
const TOOL_RAIL_WIDTH = pxToRem(1);
const railColor = theme.color.border10;
const TOOL_RAIL_LEFT = `calc((${TOOL_RAIL_COLUMN_WIDTH} - ${TOOL_RAIL_WIDTH}) / 2)`;

export type ToolActionProps = {
  onSubmitAnswers: (answers: QuestionAnswer[]) => Promise<void>;
  onPickResource: (selection: { id: string; name: string }) => Promise<void>;
  onAcceptPlan: () => Promise<void>;
  onStartReview: () => void | Promise<unknown>;
  hasReviewStarted: boolean;
  isReviewLoading?: boolean;
  onApproveEnterPlanMode: () => Promise<void>;
  onRejectEnterPlanMode: () => Promise<void>;
  onSubmitMarketplaceAuth: (
    results: { templateShortId: string; isConnected: boolean }[]
  ) => Promise<void>;
  isPlanLoading?: boolean;
  isModeLoading?: boolean;
  isMarketplaceAuthLoading?: boolean;
};

type ToolMessageProps = ToolActionProps & {
  toolName?: string;
  toolLabel?: string;
  toolDescription?: string;
  status: ToolStatus;
  data?: ToolData;
  errorMessage?: string;
  isNextTool: boolean;
  isDescriptionOpen: boolean;
  onToggleDescription: () => void;
};

export const ToolMessage = ({
  toolName,
  toolLabel,
  toolDescription,
  status,
  data,
  errorMessage,
  isNextTool,
  isDescriptionOpen,
  onToggleDescription,
  onSubmitAnswers,
  onPickResource,
  onAcceptPlan,
  onStartReview,
  hasReviewStarted,
  isReviewLoading,
  onApproveEnterPlanMode,
  onRejectEnterPlanMode,
  onSubmitMarketplaceAuth,
  isPlanLoading,
  isModeLoading,
  isMarketplaceAuthLoading,
}: ToolMessageProps) => {
  const collapsedContent = errorMessage || toolDescription;

  if (toolName === 'ask_user_questions' && status === 'pending' && data?.type === 'questions') {
    return (
      <AiBuilderQuestions
        questions={data.questions}
        onSubmit={(answers) => {
          return onSubmitAnswers(answers);
        }}
      />
    );
  }

  if (toolName === 'exit_plan_mode' && data?.type === 'plan') {
    let planStatus: 'pending' | 'accepted' | 'rejected' = 'pending';
    if (status === 'accepted' || status === 'done') {
      planStatus = 'accepted';
    } else if (status === 'rejected' || status === 'error') {
      planStatus = 'rejected';
    }
    return (
      <AiBuilderPlan
        plan={data.plan}
        status={planStatus}
        onAccept={onAcceptPlan}
        isLoading={isPlanLoading}
      />
    );
  }

  if (toolName === 'enter_plan_mode') {
    let modeStatus: 'pending' | 'accepted' | 'rejected' = 'pending';
    if (status === 'accepted' || status === 'done') {
      modeStatus = 'accepted';
    } else if (status === 'rejected' || status === 'error') {
      modeStatus = 'rejected';
    }
    return (
      <AiBuilderEnterPlanMode
        status={modeStatus}
        onApprove={onApproveEnterPlanMode}
        onReject={onRejectEnterPlanMode}
        isLoading={isModeLoading}
      />
    );
  }

  if (toolName === 'prompt_marketplace_auth' && data?.type === 'marketplace_auth') {
    let authStatus: 'pending' | 'accepted' | 'rejected' = 'pending';
    if (status === 'accepted' || status === 'done') {
      authStatus = 'accepted';
    } else if (status === 'rejected' || status === 'error') {
      authStatus = 'rejected';
    }
    return (
      <AiBuilderMarketplaceAuth
        integrations={data.integrations}
        status={authStatus}
        onSubmit={onSubmitMarketplaceAuth}
        isLoading={isMarketplaceAuthLoading}
      />
    );
  }

  if (toolName === 'pick_resource' && status === 'pending' && data?.type === 'resource_picker') {
    return (
      <AiBuilderResourcePicker
        resourcePicker={data.resourcePicker}
        onPick={(selection) => onPickResource({ id: selection.id, name: selection.name })}
      />
    );
  }

  if (toolName === 'start_journey_review' && data?.type === 'journey_review_prompt') {
    return (
      <AiBuilderReviewPrompt
        isRunning={hasReviewStarted}
        onStart={onStartReview}
        isLoading={isReviewLoading}
      />
    );
  }

  if (toolName === 'show_portal_link' && data?.type === 'portal_link') {
    return <AiBuilderLinkPreview portalLink={data.portalLink} />;
  }

  const showDescription = isDescriptionOpen && Boolean(collapsedContent);
  return (
    <ToolGroup $extendGap={isNextTool}>
      <ToolRow>
        <ToolRailColumn $centerIcon={!isNextTool}>
          <ToolIconWrapper>
            {status === 'done' ? (
              <IconCheckmark color="elementGreen100" />
            ) : status === 'error' || status === 'rejected' ? (
              <IconCloseRegular color="elementRed100" />
            ) : (
              <SizedRelativeSpinner size={16} $skipMargin $borderWidth={1.5} />
            )}
          </ToolIconWrapper>
          {isNextTool && <ToolRailLine />}
        </ToolRailColumn>
        <ToolContent>
          <ToolHeader>
            <Text font="semibold12" text={toolLabel} />
            {collapsedContent && (
              <IconDropside
                color="buttonIconPositive"
                cursor="pointer"
                rotateDegrees={isDescriptionOpen ? 90 : 0}
                onClick={(e) => {
                  e.stopPropagation?.();
                  onToggleDescription();
                }}
              />
            )}
          </ToolHeader>
        </ToolContent>
      </ToolRow>

      <Collapse isCollapsed={!showDescription} overflowType="visible">
        <ToolRow>
          <ToolRailColumn>{isNextTool && <ToolRailLine />}</ToolRailColumn>
          <ToolContent>
            <div>
              <Text
                font="regular12"
                text={collapsedContent}
                color={errorMessage ? 'elementRed100' : 'textPrimary'}
              />
            </div>
          </ToolContent>
        </ToolRow>
      </Collapse>
    </ToolGroup>
  );
};

const ToolGroup = styled.div<{ $extendGap?: boolean }>(({ $extendGap }) => ({
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  '&::after': $extendGap
    ? {
        content: '""',
        position: 'absolute',
        top: '100%',
        left: TOOL_RAIL_LEFT,
        width: TOOL_RAIL_WIDTH,
        height: MESSAGE_GAP,
        backgroundColor: railColor,
      }
    : undefined,
}));

const ToolRow = styled(Stack).attrs({
  orientation: 'horizontal',
  align: 'stretch',
  space: 2,
})({});

const ToolRailColumn = styled.div<{ $centerIcon?: boolean }>(({ $centerIcon }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: $centerIcon ? 'center' : 'flex-start',
  width: TOOL_RAIL_COLUMN_WIDTH,
  flexShrink: 0,
  position: 'relative',
}));

const ToolRailLine = styled.div({
  width: TOOL_RAIL_WIDTH,
  flex: '1 1 auto',
  backgroundColor: railColor,
  minHeight: pxToRem(8),
});

const ToolIconWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: pxToRem(20),
  height: pxToRem(20),
  flexShrink: 0,
  zIndex: 1,
  backgroundColor: theme.color.backgroundLightDark,
});

const ToolContent = styled.div({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  minWidth: 0,
});

const ToolHeader = styled(Stack).attrs({
  orientation: 'horizontal',
  align: 'center',
  space: 2,
})({
  minHeight: pxToRem(20),
});
